import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ClampDefinition } from '../models/clamp-definition.model';
import { InstallationApiService } from './installation-api.service';

@Injectable({
  providedIn: 'root'
})
export class ClampDefinitionService {
  private clampDefinitionsMap: Map<string, ClampDefinition> = new Map();
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private loadedSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<string>('');

  // Expose observables for components to subscribe to
  public loading$ = this.loadingSubject.asObservable();
  public loaded$ = this.loadedSubject.asObservable();
  public error$ = this.errorSubject.asObservable();

  constructor(private installationService: InstallationApiService) {}

  /**
   * Initialize the clamp definitions lookup table
   * This should be called when the application starts
   */
  public initializeClampDefinitions(): Observable<boolean> {
    // If already loaded, return immediately
    if (this.loadedSubject.value) {
      return of(true);
    }

    this.loadingSubject.next(true);
    this.errorSubject.next('');

    return this.installationService.getClampDefinitions().pipe(
      tap(clampDefinitions => {
        // Clear the map first
        this.clampDefinitionsMap.clear();

        // Populate the map with clamp definitions
        clampDefinitions.forEach(clampDef => {
          this.clampDefinitionsMap.set(clampDef.uuid, clampDef);
        });

        console.log(`Loaded ${this.clampDefinitionsMap.size} clamp definitions`);
        this.loadingSubject.next(false);
        this.loadedSubject.next(true);
      }),
      map(() => true),
      catchError(error => {
        console.error('Error loading clamp definitions:', error);
        this.errorSubject.next(`Error loading clamp definitions: ${error.message || 'Unknown error'}`);
        this.loadingSubject.next(false);
        return of(false);
      })
    );
  }

  /**
   * Get a clamp definition label by UUID
   * @param uuid The UUID of the clamp definition
   * @returns The label of the clamp definition, or the UUID if not found
   */
  public getClampDefinitionLabel(uuid: string): string {
    const clampDef = this.clampDefinitionsMap.get(uuid);
    return clampDef ? clampDef.label : uuid;
  }

  /**
   * Get a clamp definition id by UUID
   * @param uuid The UUID of the clamp definition
   * @returns The id of the clamp definition, or the UUID if not found
   */
  public getClampDefinitionId(uuid: string): string {
    const clampDef = this.clampDefinitionsMap.get(uuid);
    return clampDef ? clampDef.id : uuid;
  }

  /**
   * Get all clamp definitions
   * @returns An array of all clamp definitions
   */
  public getAllClampDefinitions(): ClampDefinition[] {
    return Array.from(this.clampDefinitionsMap.values());
  }

  /**
   * Check if the clamp definitions have been loaded
   * @returns True if the clamp definitions have been loaded
   */
  public isLoaded(): boolean {
    return this.loadedSubject.value;
  }

  /**
   * Check if the clamp definitions are currently loading
   * @returns True if the clamp definitions are currently loading
   */
  public isLoading(): boolean {
    return this.loadingSubject.value;
  }

  /**
   * Get the error message if there was an error loading the clamp definitions
   * @returns The error message, or an empty string if there was no error
   */
  public getError(): string {
    return this.errorSubject.value;
  }
}
